import React from 'react'
import { Image } from '@/atoms/Image'
import { InternalLink } from '@/atoms/InternalLink'
import { AngelLogo } from '@/atoms/Logos/AngelLogo'
import { ResponsiveBackgroundImage } from '@/atoms/ResponsiveBackgroundImage'
import { AsH1, ParagraphSM, ParagraphMD, HeadingSM } from '@/atoms/Text'
import { PRICE_ANCHOR_EXPERIMENT_VARIANTS } from '@/constants/guild'
import { paths } from '@/constants/paths'
import { getCloudinaryImageUrl } from '@/utils/Cloudinary'
import { Translate, useTranslate } from '@/utils/translate/translate-client'
import { OmniGuildPlanSelectionCard } from '@/views/GuildJoinView/OmniGuildPlanSelectionCard'
import { useGuildSignupContext } from '@/views/GuildSignupView/GuildSignupContext'

interface GuildBlackFridayHeroProps {
  hasUser: boolean
  saleBackgroundClass?: string
  showFreeShowsCta?: boolean
}

const DESKTOP_BACKGROUND = '/v1764189899/angel-studios/guild/guild-join/angel-key-titles-bg.webp'
const MOBILE_BACKGROUND = '/v1764178994/angel-studios/guild/guild-join/angel-key-titles-bg-mobile.webp'

export const GuildBlackFridayHero: React.FC<GuildBlackFridayHeroProps> = ({
  hasUser,
  saleBackgroundClass,
  showFreeShowsCta = false,
}) => {
  const { experiments } = useGuildSignupContext()
  const { t } = useTranslate('guild')
  const shouldDefaultToPremium = Boolean(experiments?.['pricing-default-premium'])
  const showWatchlistLink = Boolean(experiments?.['free-signup-flow-watchlist'])
  const freeShowsHref = showWatchlistLink ? paths.watchlist.index : paths.watch.index
  const shouldShowFreeShowsCta = showFreeShowsCta && !hasUser
  const saleDescription = t(
    'blackFridaySaleDescription',
    "Don't miss out on movies and shows that uplift & amplify light. Sign up now and lock in your savings before the sale ends!",
  )
  const ARImage = 754 / 1009
  const responsiveAR = {
    xs: 0,
    sm: 0,
    md: 0,
    lg: ARImage,
    xl: ARImage,
    '2xl': ARImage,
    '3xl': ARImage,
  }

  return (
    <section
      className={`relative mb-60 mt-[64px] w-full overflow-hidden px-0 pb-12 pt-0 lg:px-12 lg:pt-12 ${saleBackgroundClass}`}
    >
      <Image
        priority
        width={1000}
        height={194}
        alt="Angel Guild Key Titles"
        className="block h-full w-full object-cover lg:hidden"
        src={getCloudinaryImageUrl({
          path: MOBILE_BACKGROUND,
          width: 1000,
        })}
      />
      <div className="relative flex w-full flex-col gap-10 overflow-hidden bg-info-200 p-4 lg:gap-14 lg:rounded-2xl lg:px-10 xl:px-12">
        <div className="absolute inset-0 hidden h-full w-full lg:block">
          <ResponsiveBackgroundImage
            src={DESKTOP_BACKGROUND}
            backgroundPosition="bg-left"
            backgroundSize="bg-contain"
            responsiveAR={responsiveAR}
          />
        </div>

        <div className="z-10 ml-auto flex w-full flex-col items-center px-0 py-4 text-center text-core-gray-950 lg:w-[60%] xl:w-1/2">
          <AngelLogo color="black" height={55} className="mb-6" />
          <HeadingSM as={AsH1} weight="bold" className="lg:photon-display text-core-gray-950">
            <Translate i18nKey="blackFridaySaleHeading" t={t}>
              Black Friday Sale
            </Translate>
          </HeadingSM>
          <ParagraphMD weight="medium" className="mt-0 text-core-gray-900">
            <Translate i18nKey="blackFridaySaleSubheading" t={t}>
              Plans starting at $6.99/mo for 12 months
            </Translate>
          </ParagraphMD>
          <ParagraphMD className="mt-4 text-core-gray-800">{saleDescription}</ParagraphMD>
          <div className="mt-4">
            <OmniGuildPlanSelectionCard
              hideLabels
              guildOfferPackage={PRICE_ANCHOR_EXPERIMENT_VARIANTS['18-169 / 20-179']}
              defaultSelectedOffer={shouldDefaultToPremium ? 'premium' : 'basic'}
            />
          </div>

          {shouldShowFreeShowsCta && (
            <div className="mt-4 flex flex-col items-center text-center">
              <ParagraphSM className="text-center text-core-gray-900 lg:text-left">
                Not interested in a paid membership?
              </ParagraphSM>
              <ParagraphSM className="text-center text-core-gray-900 lg:text-left">
                <Translate t={t} i18nKey="clickHereToWatchFreeShows">
                  Click{' '}
                  <InternalLink href={freeShowsHref} className="underline">
                    here
                  </InternalLink>{' '}
                  to watch our free shows & films
                </Translate>
              </ParagraphSM>
            </div>
          )}
        </div>
      </div>
    </section>
  )
}
